import { Authority, User } from '@project44/ui-utils';
import { UserEnums } from '@project44-models/iam-gateway';

import { UIFormClient } from '../../../../../external/UIFormClient/domain/abstract/UIFormClient';
import { CheckboxFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/CheckboxFormProperty';
import { DropdownFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/DropdownFormProperty';
import { InputFieldFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/InputFieldFormProperty';
import { TenantAdminUserContactPersonCheckbox } from '../../../infrastructure/ui/FormFields/TenantAdminUserContactPersonCheckbox/TenantAdminUserContactPerson.checkbox';
import { UserPasswordComponent } from '../../../infrastructure/ui/FormFields/UserPassword/UserPassword.component';
import { IUserCreationForm } from '../../interfaces';

export interface IUserCreationFormTemplate {
  properties: UIFormClient<IUserCreationForm>['properties'];
  transformUserCreationForm: (
    userCreationForm: IUserCreationForm,
  ) => IUserCreationForm;
}

export abstract class UserCreationFormTemplate {
  public abstract availableUserTypes: Array<UserEnums.UserTypeEnum> | undefined;
  public transformUserCreationForm(
    userCreationForm: IUserCreationForm,
  ): IUserCreationForm {
    return userCreationForm;
  }

  public compose(userPrincipal: User): IUserCreationFormTemplate {
    const canAssignTenantSpecificRoles = userPrincipal.hasAnyOfPermissions([
      <Authority>'system-admin:update:tenant-specific-user-role-assignments',
      <Authority>'SAVE_USER_ROLES',
    ]);

    const emptyInputField = {
      initialValue: '',
      isDisplayable: true,
      isEditable: true,
    };

    return {
      transformUserCreationForm: this.transformUserCreationForm,
      properties: {
        username: InputFieldFormProperty.create(
          { label: 'Email' },
          emptyInputField,
        ),
        givenName: InputFieldFormProperty.create(
          { label: 'Given name' },
          emptyInputField,
        ),
        familyName: InputFieldFormProperty.create(
          { label: 'Family name' },
          emptyInputField,
        ),
        password: UserPasswordComponent.create(
          {
            radioGroupProps: {
              value: 'DELEGATED',
            },
            radioButtons: [
              {
                value: 'DELEGATED',
                children: 'User will choose their own password',
              },
              {
                value: 'setPassword',
                children: 'Set a password for this user',
              },
            ],
          },
          { isDisplayable: true, isEditable: true },
          { label: 'Password', value: 'DELEGATED' },
        ),
        phoneNumber: InputFieldFormProperty.create(
          { label: 'Phone number (optional)' },
          emptyInputField,
        ),
        userType:
          this.availableUserTypes === undefined
            ? TenantAdminUserContactPersonCheckbox.compose({
                initialValue: UserEnums.UserTypeEnum.USER,
                isDisplayable: true,
                isEditable: true,
                label: 'Assign as Contact Person',
                helperText:
                  'Contact persons are visible for other companies when requesting data sharing. Since contact persons can receive invitations, they must be Administrators to accept/reject invitations.',
              })
            : DropdownFormProperty.compose(
                this.availableUserTypes.map((userTypeEnum) => {
                  return {
                    displayValue: this.translateUserTypeEnum(userTypeEnum),
                    value: userTypeEnum,
                  };
                }),
                {
                  initialValue: UserEnums.UserTypeEnum.USER,
                  isDisplayable: true,
                  isEditable: true,
                },
              ),
        isAdmin: CheckboxFormProperty.compose(
          {
            initialValue: 'false',
            isDisplayable: canAssignTenantSpecificRoles,
            isEditable: canAssignTenantSpecificRoles,
            label: 'Administrator',
            helperText:
              'Access to all higher-level functions, such as inviting carriers, adding new ELD/GPS connections and creating subscriptions.',
          },
          { children: 'Administrator' },
        ),
      },
    };
  }

  private translateUserTypeEnum = (
    userType: UserEnums.UserTypeEnum,
  ): string => {
    const {
      USER,
      CONTACT_PERSON,
      CUSTOMER_INTEGRATION_USER,
      SYSTEM_USER,
      COMPANY_CREATOR,
      CLIENT_APPLICATION_USER,
      DATA_STREAM_USER,
      IAS,
    } = UserEnums.UserTypeEnum;

    const enumMap: Record<UserEnums.UserTypeEnum, string> = {
      [USER]: 'User',
      [CONTACT_PERSON]: 'Contact Person',
      [CUSTOMER_INTEGRATION_USER]: 'Customer Integration User',
      [SYSTEM_USER]: 'System User',
      [COMPANY_CREATOR]: 'Company Creator',
      [CLIENT_APPLICATION_USER]: 'Client Application User',
      [DATA_STREAM_USER]: 'Data Stream User',
      [IAS]: 'IAS',
    };

    return enumMap[userType];
  };
}
